import { memo } from 'react';

import { Stack, useTheme } from '@mui/material';
import { endOfDay, formatDistanceStrict, isToday } from 'date-fns';
import {
  ClipboardCheck,
  DoorClosed,
  DoorOpen,
  type LucideIcon,
  Phone,
  PhoneMissed,
  StickyNote,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';

import type { LeadsListItemFragment } from '@graphql/__generated__/graphql';
import { activityNotePlainText } from '@modules/_shared/utils/activity';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

export const ActivityCell = memo(
  ({
    node,
    type,
  }: {
    node: LeadsListItemFragment;
    type: 'next' | 'latest';
  }) => {
    const { t } = useLocale();
    const theme = useTheme();
    const palette = (theme.vars ?? theme).palette;

    const activity =
      type === 'latest' ? node?.last_activity?.[0] : node?.next_activity?.[0];

    if (activity == null) {
      return (
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <TriangleAlert
            size={16}
            color={palette.error.main}
            style={{ marginRight: 4 }}
          />
          {t('No activity')}
        </Stack>
      );
    }

    let activityDisplayDate: Date | null = null;
    let isTodayActivity = false;
    let overdue = false;

    if (type === 'latest') {
      activityDisplayDate = endOfDay(new Date(activity.consolidated_done_at));
      isTodayActivity = isToday(new Date(activity.consolidated_done_at));
    } else {
      if (activity.due_at != null) {
        activityDisplayDate = new Date(activity.due_at);
      } else {
        const fallbackDate = new Date(
          activity.start_date ?? activity.created_at,
        );
        activityDisplayDate = endOfDay(fallbackDate);
        isTodayActivity = isToday(fallbackDate);
      }

      if (activity.activity_type !== 'note') {
        overdue = activityDisplayDate < new Date();
      }
    }

    switch (activity.activity_type) {
      case 'task':
        return (
          <ActivityCellContent
            Icon={ClipboardCheck}
            content={
              activity.subject == null || activity.subject.trim() === ''
                ? t('task')
                : activity.subject
            }
            overdue={overdue}
            activityDate={activityDisplayDate}
            displayToday={isTodayActivity}
          />
        );
      case 'call': {
        const { note, success, done } = activity;
        const Icon = success === false && done === true ? PhoneMissed : Phone;

        return (
          <ActivityCellContent
            Icon={Icon}
            content={activityNotePlainText(note) || t('call')}
            overdue={overdue}
            activityDate={activityDisplayDate}
            displayToday={isTodayActivity}
          />
        );
      }
      case 'visit': {
        const { note, success, done } = activity;
        const Icon = success === false && done === true ? DoorClosed : DoorOpen;
        return (
          <ActivityCellContent
            Icon={Icon}
            content={activityNotePlainText(note) || t('visit')}
            overdue={overdue}
            activityDate={activityDisplayDate}
            displayToday={isTodayActivity}
          />
        );
      }
      case 'note':
        return (
          <ActivityCellContent
            Icon={StickyNote}
            content={activityNotePlainText(activity.note) || t('Note')}
            overdue={overdue}
            activityDate={activityDisplayDate}
            displayToday={isTodayActivity}
          />
        );
      case 'assignment':
        return (
          <ActivityCellContent
            Icon={UserCheck}
            content={type === 'latest' ? t('leadClaimed') : t('claimLead')}
            overdue={overdue}
            activityDate={activityDisplayDate}
            displayToday={isTodayActivity}
          />
        );
      default:
        return null;
    }
  },
);

const ActivityCellContent: React.FC<{
  Icon: LucideIcon;
  content: React.ReactNode;
  overdue: boolean;
  activityDate: Date | null;
  displayToday?: boolean;
}> = ({ Icon, content, overdue, activityDate, displayToday }) => {
  const theme = useTheme();
  const { text } = theme;
  const palette = (theme.vars ?? theme).palette;
  const { t, dateLocale } = useLocale();
  const overdueStyles = overdue ? { color: palette.secondary.main } : null;

  return (
    <div css={overdueStyles}>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Icon
          size={16}
          color={overdue ? palette.secondary.main : customPalette.mediumText}
          style={{ marginRight: 4, flexShrink: 0 }}
        />
        <div css={text.ellipsis}>{content}</div>
      </Stack>
      {displayToday ? (
        t('Today')
      ) : (
        <div
          css={{ color: overdue ? palette.secondary.main : palette.grey[700] }}
        >
          {activityDate != null
            ? formatDistanceStrict(activityDate, Date.now(), {
                locale: dateLocale,
                addSuffix: true,
              })
            : '-'}
        </div>
      )}
    </div>
  );
};
